import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, ChevronDown, Pencil } from "lucide-react";
import { AddNotesSheet, EditSessionSheet } from "@/components/SessionEditors";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { OrganiseCard } from "@/components/OrganiseCard";
import { PageHeader } from "@/components/PageHeader";
import { aiLeftOf, usePlan } from "@/lib/plan";
import { SessionCard } from "@/components/SessionCard";

export default function SessionPage() {
  const { data: plan } = usePlan();
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { setUser } = useAuth();
  const [showRaw, setShowRaw] = useState(false);
  const [sheet, setSheet] = useState(null);
  const { data: s, isLoading, isError } = useQuery({
    queryKey: ["session", id],
    queryFn: () => api.get(`/sessions/${id}`).then((r) => r.data),
    initialData: () => qc.getQueriesData({ queryKey: ["sessions"] }).flatMap(([, d]) => d || []).find((x) => x.id === id),
    initialDataUpdatedAt: 0,
  });

  const remove = async () => {
    try {
      await api.delete(`/sessions/${id}`);
      const me = await api.get("/auth/me");
      setUser(me.data);
      qc.invalidateQueries();
      toast.success("Session deleted");
      navigate("/journal", { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div>
      <PageHeader back title="Session" />
      <div className="px-5 space-y-4">
        {isLoading && <div className="card h-64 animate-pulse" />}
        {isError && !s && <div className="card p-6 text-sm text-[#A1A1AA]" data-testid="session-not-found">Session not found.</div>}
        {s && (
          <>
            <SessionCard session={s} />
            {s.ai_organised === false && <OrganiseCard session={s} />}
            <div className={`grid grid-cols-2 gap-2.5 ${s.ai_organised === false ? "hidden" : ""}`}>
              <button data-testid="session-edit-button" onClick={() => setSheet("edit")} className="btn-secondary"><Pencil size={16} /> Edit</button>
              <button data-testid="session-add-notes-button" onClick={() => setSheet("add")} className="btn-secondary">Add notes</button>
            </div>
            <p className="text-[11px] text-[#71717A] -mt-1 text-center" data-testid="session-ai-note">{aiLeftOf(plan) === null ? "Editing is free. AI analysis is unlimited on Pro." : "Editing is free. Adding notes with AI uses 1 of your free weekly analyses."}</p>
            <EditSessionSheet session={s} open={sheet === "edit"} onOpenChange={(v) => setSheet(v ? "edit" : null)} />
            <AddNotesSheet session={s} open={sheet === "add"} onOpenChange={(v) => setSheet(v ? "add" : null)} />
            <div className="card">
              <button data-testid="session-raw-toggle" onClick={() => setShowRaw(!showRaw)} className="w-full p-4 flex items-center justify-between text-sm font-semibold">
                Your original note
                <ChevronDown size={18} className={`transition-transform duration-200 ${showRaw ? "rotate-180" : ""}`} />
              </button>
              {showRaw && <p data-testid="session-raw-text" className="px-4 pb-4 text-sm text-[#A1A1AA] whitespace-pre-wrap leading-relaxed">{s.raw_text}</p>}
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="session-delete-button" className="w-full h-12 text-sm text-[#71717A] hover:text-[#FF3B30] flex items-center justify-center gap-2 transition-colors">
                  <Trash2 size={16} /> Delete session
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#161618] border-[#27272A] max-w-[90vw] sm:max-w-sm rounded-2xl">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this session?</AlertDialogTitle>
                  <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="session-delete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="session-delete-confirm" onClick={remove} className="bg-[#E50914] hover:bg-[#B20710] text-white">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  );
}
